import React, { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { getApiUrl } from "../config/api";
import {
  BellIcon,
  ShieldCheckIcon,
  ServerIcon,
} from "@heroicons/react/24/outline";

interface SettingSection {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  settings: Setting[];
}

interface Setting {
  id: string;
  label: string;
  description: string;
  type: "toggle" | "input" | "select";
  value: any;
  options?: { value: string; label: string }[];
}

// imports were moved to the top to satisfy import/first rule

export const SettingsPage: React.FC = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState<Record<string, any>>({
    notifications: true,
    emailNotifications: true,
    autoBackup: true,
    backupFrequency: "daily",
    maxFileSize: "10",
    sessionTimeout: "30",
  });

  const settingSections: SettingSection[] = [
    {
      id: "notifications",
      title: "Уведомления",
      description: "Настройки уведомлений и оповещений",
      icon: BellIcon,
      settings: [
        {
          id: "notifications",
          label: "Включить уведомления",
          description: "Получать уведомления о важных событиях",
          type: "toggle",
          value: settings.notifications,
        },
        {
          id: "emailNotifications",
          label: "Email уведомления",
          description: "Отправлять уведомления на email",
          type: "toggle",
          value: settings.emailNotifications,
        },
      ],
    },
    {
      id: "security",
      title: "Безопасность",
      description: "Настройки безопасности и авторизации",
      icon: ShieldCheckIcon,
      settings: [
        {
          id: "sessionTimeout",
          label: "Таймаут сессии (минуты)",
          description: "Время автоматического выхода из системы",
          type: "input",
          value: settings.sessionTimeout,
        },
      ],
    },
    // Секция «Система» удалена по требованиям
    {
      id: "backup",
      title: "Резервное копирование",
      description: "Настройки автоматического резервного копирования",
      icon: ServerIcon,
      settings: [
        {
          id: "autoBackup",
          label: "Автоматическое резервное копирование",
          description: "Включить автоматическое создание резервных копий",
          type: "toggle",
          value: settings.autoBackup,
        },
        {
          id: "backupFrequency",
          label: "Частота резервного копирования",
          description: "Как часто создавать резервные копии",
          type: "select",
          value: settings.backupFrequency,
          options: [
            { value: "daily", label: "Ежедневно" },
            { value: "weekly", label: "Еженедельно" },
            { value: "monthly", label: "Ежемесячно" },
          ],
        },
      ],
    },
  ];

  const handleSettingChange = (
    sectionId: string,
    settingId: string,
    value: any
  ) => {
    setSettings((prev) => ({
      ...prev,
      [settingId]: value,
    }));
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(getApiUrl("/api/admin/settings"), {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        if (res.ok) {
          const data = await res.json();
          // Исключаем удаленные настройки
          const { language, timezone, ...rest } = (data || {}) as Record<
            string,
            any
          >;
          setSettings((prev) => ({ ...prev, ...rest }));
        }
      } catch {}
    };
    fetchSettings();
  }, [token]);

  const renderSetting = (setting: Setting, sectionId: string) => {
    switch (setting.type) {
      case "toggle":
        return (
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900">
                {setting.label}
              </label>
              <p className="text-sm text-gray-500">{setting.description}</p>
            </div>
            <button
              onClick={() =>
                handleSettingChange(sectionId, setting.id, !setting.value)
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                setting.value ? "bg-primary-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  setting.value ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );

      case "input":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              {setting.label}
            </label>
            <input
              type="text"
              className="input-field"
              value={setting.value}
              onChange={(e) =>
                handleSettingChange(sectionId, setting.id, e.target.value)
              }
            />
            <p className="mt-1 text-sm text-gray-500">{setting.description}</p>
          </div>
        );

      case "select":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              {setting.label}
            </label>
            {/* eslint-disable-next-line @typescript-eslint/no-var-requires */}
            {(() => {
              // Динамический импорт для изоляции компонента в этой ветке рендера
              const { Select } = require("../components/Select");
              return (
                <Select
                  value={String(setting.value)}
                  onChange={(val: string) =>
                    handleSettingChange(sectionId, setting.id, val)
                  }
                  options={setting.options || []}
                />
              );
            })()}
            <p className="mt-1 text-sm text-gray-500">{setting.description}</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Настройки</h1>
          <p className="mt-1 text-sm text-gray-500">
            Управление настройками системы
          </p>
        </div>

        {/* Settings Sections */}
        <div className="space-y-6">
          {settingSections.map((section) => (
            <div key={section.id} className="card">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <section.icon className="h-6 w-6 text-primary-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {section.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {section.description}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 space-y-6">
                {section.settings.map((setting) => (
                  <div
                    key={setting.id}
                    className="border-b border-gray-100 last:border-b-0 pb-4 last:pb-0"
                  >
                    {renderSetting(setting, section.id)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            className="btn-primary"
            onClick={async () => {
              try {
                // Отправляем только разрешенные настройки
                const {
                  notifications,
                  emailNotifications,
                  autoBackup,
                  backupFrequency,
                  sessionTimeout,
                  maxFileSize,
                } = settings;
                await fetch(getApiUrl("/api/admin/settings"), {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: token ? `Bearer ${token}` : "",
                  },
                  body: JSON.stringify({
                    notifications,
                    emailNotifications,
                    autoBackup,
                    backupFrequency,
                    sessionTimeout,
                    maxFileSize,
                  }),
                });
              } catch {}
            }}
          >
            Сохранить настройки
          </button>
        </div>
      </div>
    </Layout>
  );
};
